import { useCallback, useEffect, useRef, useState } from 'react';
import { acceptInvitation } from '../lib/api.js';
import { describeFailure } from '../lib/invitation.js';
import { ConsentBanner } from '../components/ConsentBanner.js';
import { LegalFooter } from '../components/LegalFooter.js';
import { Logo } from '../design/index.js';

/**
 * Accepting an invitation.
 *
 * This is the only screen that turns somebody who can sign in at the identity
 * provider into somebody who has an account here. It is also the first thing
 * the first owner of the first salon ever sees, because the establishment is
 * created by CI and the invitation is how its owner gets in.
 *
 * ## Why the token is in the fragment
 *
 * The link is `/invitations/accept#token=…`, not `?token=…`, and that is the
 * point. A fragment is never sent to a server: not to ours, not in a `Referer`
 * header to anyone the page later links to, and not into any access log along
 * the way. A query string would put a live credential — one that makes its
 * holder the owner of a salon — into every log between the browser and us.
 *
 * It is read once, kept out of the address bar from the first paint, and
 * discarded the moment it has been used.
 *
 * ## Why it survives the sign-in
 *
 * Most people arrive here signed out: they clicked a link in an e-mail. Signing
 * in is a full navigation to the provider and back, which loses the fragment,
 * so the token is parked in `sessionStorage` for the round trip and removed as
 * soon as it has been redeemed or refused. Per tab, same origin, and gone when
 * the tab closes.
 *
 * Carrying it through `return_to` instead would have put it in a URL the server
 * sees, which is the one thing the fragment exists to avoid.
 */

/** Per tab, same origin, and cleared the moment the token has been used. */
const STASH_KEY = 'gsi.invitation';

/** Where the browser comes back to after signing in. */
const ACCEPT_PATH = '/invitations/accept';

function readStash(): string | null {
  try {
    return window.sessionStorage.getItem(STASH_KEY);
  } catch {
    // Storage can be blocked outright. That is a worse experience, not a
    // broken one: somebody already signed in still completes in one step.
    return null;
  }
}

function writeStash(token: string): void {
  try {
    window.sessionStorage.setItem(STASH_KEY, token);
  } catch {
    /* see readStash */
  }
}

function clearStash(): void {
  try {
    window.sessionStorage.removeItem(STASH_KEY);
  } catch {
    /* see readStash */
  }
}

/**
 * Takes the token out of the address bar.
 *
 * `replaceState` rather than `pushState`, so the back button does not return
 * to a URL carrying a credential, and so a reload or a screenshot does not.
 */
function takeTokenFromFragment(): string | null {
  const fragment = window.location.hash.replace(/^#/, '');
  if (fragment === '') return null;

  const token = new URLSearchParams(fragment).get('token');
  if (token === null || token === '') return null;

  window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
  return token;
}

type Phase = 'ready' | 'working' | 'joined' | 'failed';

export function AcceptInvitation({
  baseUrl,
  accessToken,
  hostedLogin,
}: {
  readonly baseUrl: string;
  /** Null when nobody is signed in yet. */
  readonly accessToken: string | null;
  readonly hostedLogin: boolean;
}): React.JSX.Element {
  const [invitation, setInvitation] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>('ready');
  const [error, setError] = useState<string | null>(null);
  /** Only ever set on a deployment with no identity provider. See below. */
  const [pasted, setPasted] = useState('');
  const [pastedSession, setPastedSession] = useState<string | null>(null);
  /** False only when the invitation itself is no good; then there is nothing to retry. */
  const [retryable, setRetryable] = useState(false);
  /** An invitation is single use, so it must not be sent twice. */
  const attempted = useRef(false);

  const session = accessToken ?? pastedSession;

  useEffect(() => {
    const fromFragment = takeTokenFromFragment();
    if (fromFragment !== null) writeStash(fromFragment);
    setInvitation(fromFragment ?? readStash());
  }, []);

  const redeem = useCallback(
    async (token: string, session: string): Promise<void> => {
      setPhase('working');
      try {
        await acceptInvitation(baseUrl, session, token);
        clearStash();
        setPhase('joined');
      } catch (cause: unknown) {
        const failure = describeFailure(cause);
        // Only when the invitation itself is no good. Discarding it on every
        // refusal is what turned a provider misconfiguration into a lost
        // invitation, with the only other copy in a CI log. See
        // lib/invitation.ts.
        if (!failure.keepToken) clearStash();
        setRetryable(failure.keepToken);
        setError(failure.message);
        setPhase('failed');
      }
    },
    [baseUrl],
  );

  /** Re-attempts with the token still in hand — after signing in again, or
   * after somebody has fixed what was refusing it. */
  const retry = useCallback((): void => {
    if (invitation === null || session === null) return;
    void redeem(invitation, session);
  }, [invitation, session, redeem]);

  useEffect(() => {
    if (invitation === null || session === null || attempted.current) return;
    attempted.current = true;
    void redeem(invitation, session);
  }, [invitation, session, redeem]);

  if (invitation === null) {
    return (
      <Panel title="Invitation introuvable">
        <p className="text-ui-sm text-ink-muted">
          Ce lien ne contient pas d’invitation. Ouvrez le lien complet qui vous a été envoyé — il se
          termine par <code className="rounded-control bg-surface-muted px-1">#token=…</code>.
        </p>
      </Panel>
    );
  }

  if (session === null) {
    return (
      <Panel title="Connectez-vous pour accepter">
        <p className="text-ui-sm text-ink-muted">
          Votre invitation est valide. Connectez-vous avec l’adresse à laquelle elle a été envoyée :
          l’invitation n’est acceptée que par cette adresse.
        </p>
        {hostedLogin ? (
          // A link, not a fetch: the provider answers with its own page, and a
          // redirect chain followed by XMLHttpRequest ends nowhere a person can
          // type a password.
          <a
            href={`${baseUrl}/v1/auth/login?return_to=${encodeURIComponent(ACCEPT_PATH)}`}
            className="mt-6 flex min-h-[44px] w-full items-center justify-center rounded-control bg-teal px-4 text-center text-ui font-medium text-ink"
          >
            Se connecter
          </a>
        ) : (
          /*
            The same field the sign-in screen offers when no provider is
            configured, and for the same reason: on a local run or in the
            end-to-end suite there is nowhere to be redirected to. Without it
            this deployment can sign somebody in and then refuse to let them
            join anything, which is a dead end rather than a control.
          */
          <form
            className="mt-6 space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (pasted.trim() !== '') setPastedSession(pasted.trim());
            }}
          >
            <input
              type="password"
              value={pasted}
              onChange={(e) => {
                setPasted(e.target.value);
              }}
              placeholder="Jeton"
              autoComplete="off"
              className="min-h-[44px] w-full rounded-control border border-line-control px-3 text-ui"
            />
            <button
              type="submit"
              className="min-h-[44px] w-full rounded-control bg-teal px-4 text-ui font-medium text-ink"
            >
              Continuer
            </button>
          </form>
        )}
      </Panel>
    );
  }

  if (phase === 'working' || phase === 'ready') {
    return (
      <Panel title="Acceptation en cours">
        <p className="text-ui-sm text-ink-muted">Un instant…</p>
      </Panel>
    );
  }

  if (phase === 'joined') {
    return (
      <Panel title="C’est fait">
        <p className="text-ui-sm text-ink-muted">
          Votre compte est maintenant rattaché à l’établissement.
        </p>
        {/*
          A full navigation rather than a state change: the dashboard reads who
          you are once, at startup, and you are not the same person to it as you
          were thirty seconds ago.
        */}
        <a
          href="/app"
          className="mt-6 flex min-h-[44px] w-full items-center justify-center rounded-control bg-teal px-4 text-center text-ui font-medium text-ink"
        >
          Ouvrir le tableau de bord
        </a>
      </Panel>
    );
  }

  return (
    <Panel title="Invitation refusée">
      <p className="rounded-card border border-danger bg-danger-wash p-4 text-ui-sm text-ink">
        {error ?? 'Une erreur est survenue.'}
      </p>
      {retryable ? (
        <>
          <p className="mt-4 text-ui-sm text-ink-muted">
            Votre invitation est conservée. Réessayez une fois le problème corrigé — inutile d’en
            demander une nouvelle.
          </p>
          <button
            type="button"
            onClick={retry}
            className="mt-4 min-h-[44px] w-full rounded-control bg-teal px-4 text-ui font-medium text-ink"
          >
            Réessayer
          </button>
        </>
      ) : (
        <p className="mt-4 text-ui-sm text-ink-muted">
          Demandez une nouvelle invitation au propriétaire de l’établissement.
        </p>
      )}
    </Panel>
  );
}

function Panel({
  title,
  children,
}: {
  readonly title: string;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="mx-auto max-w-md px-4 py-16">
      <Logo tone="light" />
      {/*
        The heading is the invitation's own, not the product's name. Somebody
        arriving from a link needs to read what happened to them, and "GSI
        Voice" as an <h1> above it tells them nothing they did not know from
        the e-mail.
      */}
      <h1 className="mt-8 text-heading font-bold text-ink">{title}</h1>
      <div className="mt-2">{children}</div>
      {/*
        Here too, and for the reason that matters most on this screen: the
        person deciding whether to join is exactly the one entitled to read
        first what we do with their data.
      */}
      <LegalFooter />
      <ConsentBanner />
    </div>
  );
}
